import { createFileRoute } from "@tanstack/react-router";
import { localProductionSection } from "@/lib/seo/local-production";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, localBusinessForCity, aeoGeo } from "@/lib/seo/schema";
import { karlstadTrust, karlstadProductionPricing, karlstadProcess } from "@/content/landing/karlstad";

const FAQ: LandingFAQ[] = [
  {
    "q": "Vad är skillnaden mellan filmproduktion och en filmbyrå?",
    "a": "Filmproduktion är hantverket. Filmbyrå inkluderar även strategi och kreativ idé. Fenny levererar båda i samma fasta pris."
  },
  {
    "q": "Hur ser en typisk filmproduktion i Karlstad ut?",
    "a": "Brief (v 1) → manus & storyboard (v 2) → pre-prod (v 3) → inspelning 1–3 dagar (v 3–4) → klipp, grade, ljudmix (v 4–6) → leverans."
  },
  {
    "q": "Vad kostar filmproduktion i Karlstad 2026?",
    "a": "Social-content: 40 000–120 000 kr. Reklamfilm 15–30 s: 150 000–450 000 kr. Brand-/industrifilm: 250 000–700 000 kr. Pulse: från 14 995 kr/mån."
  },
  {
    "q": "Hur snabbt kan ni vara igång?",
    "a": "Första möte inom 5 arbetsdagar. Pulse: första klipp inom 10–14 dagar."
  },
  {
    "q": "Var i Värmland filmar ni?",
    "a": "Hela regionen — Inre Hamn, KAU, Hammarö, Kristinehamn, Forshaga och Säffle samt Hammarö, Kristinehamn, Forshaga och hela Värmland. Drönar-tillstånd för aktuella zoner."
  },
  {
    "q": "Vilken utrustning använder ni?",
    "a": "Sony FX6/FX9 eller ARRI Alexa Mini. Aputure/ARRI-ljus. DJI-drönare. Post i DaVinci Resolve och Premiere."
  },
  {
    "q": "Klarar ni krävande industri- och säkerhetsmiljöer?",
    "a": "Ja. Vana vid pappers- och processindustri, bygg och KAU. PPE, NDA, säkerhetsbriefer och samordning med skiftledare."
  },
  {
    "q": "Tar ni hand om manus och regi?",
    "a": "Ja. Vana att översätta komplex verksamhet till säljande film."
  },
  {
    "q": "Hur många versioner ingår?",
    "a": "En master per format. Engelska versioner för export-/forskningskommunikation vid behov."
  },
  {
    "q": "Får vi raw-materialet?",
    "a": "Standard: mastrade filer + stillbilder. Raw lagras 12 månader."
  },
  {
    "q": "Kan vi vara med på set?",
    "a": "Ja. Live-feed via Frame.io för distans-kunder."
  },
  {
    "q": "Hur fungerar betalning?",
    "a": "50% vid produktionsstart, 50% vid leverans."
  }
];

export const Route = createFileRoute("/reklamfilm_/karlstad")({
  head: () => ({
    meta: [
      { title: "Reklamfilm Karlstad — fast pris och klart på 4–6 veckor" },
      { name: "description", content: "Reklamfilm i Karlstad: se exempel, pris och process. Fast pris utan resepåslag, hela kedjan inhouse, klart på 4–6 veckor. Boka ett kostnadsfritt möte." },
      { property: "og:title", content: "Reklamfilm Karlstad — fast pris, klart på 4–6 veckor" },
      { property: "og:description", content: "Premium reklamfilm i Karlstad. Hela kedjan inhouse." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/reklamfilm/karlstad" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/reklamfilm/karlstad" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/reklamfilm/karlstad", name: "Reklamfilm Karlstad — fast pris och klart på 4–6 veckor", description: "Reklamfilm i Karlstad för paid social, YouTube och TV. Fast pris, hela kedjan inhouse och leverans på 4–6 veckor. Vana vid pappers- och processindustri, bygg och KAU."})) },
      { type: "application/ld+json", children: JSON.stringify(localBusinessForCity({ city: "Karlstad", path: "/reklamfilm/karlstad", description: "Fenny producerar reklamfilm för varumärken i Karlstad och Värmland.", postalRegion: "Värmlands län" })) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Reklamfilm", path: "/reklamfilm" }, { name: "Karlstad", path: "/reklamfilm/karlstad" }])) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <LandingPage
      eyebrow="Reklamfilm · Karlstad"
      title={<>Reklamfilm i Karlstad — premiumproduktion utan storstadspåslag.</>}
      lede="Fenny gör reklamfilm för paid social, YouTube och TV i Karlstad. Pulse-abonnemang för kontinuerligt flöde eller Prime-kampanj som engångsproduktion. Vana vid pappers- och processindustri (Stora Enso, BillerudKorsnäs, Valmet), bygg, KAU och offentlig sektor."
      tldr={<p>Fenny producerar reklamfilm i <strong>Karlstad</strong> — strategi, regi, foto och versionering inhouse. <strong>Fast pris</strong>, 4–6 v. Inga reseavgifter inom Värmland.</p>}
      ctaPrimary={{ label: "Få offert inom 48 h", to: "/kontakt" }}
      ctaSecondary={{ label: "Se priser", to: "#priser" }}
      trackingId="reklamfilm-karlstad"
      trust={karlstadTrust}
      bullets={[
        { title: "Inga resepåslag", body: "Fast pris — vi tar inte extra för att åka till Karlstad." },
        { title: "Hela kedjan inhouse", body: "Strategi, manus, regi, foto, klipp och versionering under ett tak." },
        { title: "Värmland-vana", body: "Djup erfarenhet av pappers- och processindustri, bygg och KAU." },
      ]}
      pricing={{ heading: "Vad reklamfilm i Karlstad kostar", intro: "Riktpriser för 2026.", rows: karlstadProductionPricing, footnote: "Alla priser exkl. moms." }}
      process={{ heading: "Från brief till leverans på 4–6 veckor", intro: "Linjär process med skriftligt godkännande.", steps: karlstadProcess }}
      sections={[
        localProductionSection("karlstad", "Karlstad"),
        { id: "vem", heading: "Vem gör vi reklamfilm för i Karlstad?", body: <p>Pappers- och processindustri (Stora Enso, BillerudKorsnäs, Valmet), bygg, KAU och offentlig sektor i Karlstad och Hammarö, Kristinehamn, Forshaga och hela Värmland.</p> },
        { id: "format", heading: "Vilka format levererar vi?", body: <p>9:16, 1:1, 4:5 och 16:9 i ett pass. Plus undertexter, hooks-cut, thumbnails och rullande postar. <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/sociala-medier-film">Sociala medier-film →</a></p> },
        { id: "andra", heading: "Andra städer", body: <p>Vi gör även reklamfilm i <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm/stockholm">Stockholm</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm/goteborg">Göteborg</a> och <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm/orebro">Örebro</a>. Se även <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/karlstad">filmproduktion i Karlstad</a>.</p> },
      ]}
      faq={FAQ}
      caseLimit={6}
      closing={{ title: "Möts vi i Karlstad?", body: "30 minuter på ert kontor eller över video.", primary: { label: "Boka möte", to: "/kontakt" }, secondary: { label: "Räkna pris", to: "/priser" } }}
    />
  );
}
